import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Accessibility, 
  Baby, 
  Maximize2, 
  Stethoscope, 
  Shield,
  ArrowLeft,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { AiAvatar, KIOSK_MESSAGES } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const IDLE_TIMEOUT = 120; // seconds

const categories = [
  {
    id: "disabled_blue_badge",
    name: "Disabled Blue Badge",
    description: "For drivers with a valid Blue Badge",
    icon: Accessibility,
    color: "#3B82F6",
    bgColor: "rgba(59, 130, 246, 0.1)",
    requiresOCR: true,
    speechName: "Disabled Blue Badge"
  },
  {
    id: "adult_with_child",
    name: "Adult with Child in Vehicle",
    description: "For parents with children under 12",
    icon: Baby,
    color: "#EC4899",
    bgColor: "rgba(236, 72, 153, 0.1)",
    requiresOCR: false,
    speechName: "Adult with Child in Vehicle"
  },
  {
    id: "extra_wide",
    name: "Extra Wide Bay",
    description: "For larger vehicles requiring extra space",
    icon: Maximize2,
    color: "#F59E0B",
    bgColor: "rgba(245, 158, 11, 0.1)",
    requiresOCR: false,
    speechName: "Extra Wide Bay"
  },
  {
    id: "nhs_staff",
    name: "NHS Staff",
    description: "For NHS employees with valid ID",
    icon: Stethoscope,
    color: "#10B981",
    bgColor: "rgba(16, 185, 129, 0.1)",
    requiresOCR: false,
    speechName: "NHS Staff"
  },
  {
    id: "military",
    name: "Military / Ex-Military",
    description: "For active or former service members with ID",
    icon: Shield,
    color: "#6366F1",
    bgColor: "rgba(99, 102, 241, 0.1)",
    requiresOCR: false,
    speechName: "Military or Ex-Military"
  }
];

export default function KioskCategory() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const idleTimerRef = useRef(null);

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  const handleContinue = () => {
    if (selectedCategory) {
      const category = categories.find(c => c.id === selectedCategory);
      // Only pass serializable data to navigation state (exclude icon component)
      const { icon, ...serializableCategory } = category;
      navigate("/kiosk/form", { state: { category: serializableCategory } });
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F0FDF4 100%)' }} data-testid="kiosk-category">
      {/* Header */}
      <header className="flex items-center justify-between p-6 md:p-8">
        <ParkWiseLogo size="lg" />
        
        {/* Progress */}
        <div className="flex items-center gap-2">
          <div className="progress-step completed">
            <CheckCircle className="w-5 h-5" />
          </div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step active">2</div>
          <div className="progress-line w-8"></div>
          <div className="progress-step pending">3</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col px-6 md:px-12 pb-8">
        {/* Avatar */}
        <div className="flex justify-center mb-6">
          <AiAvatar 
            message={KIOSK_MESSAGES.category} 
            size="medium"
            autoSpeak={true}
          />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Select Your Parking Category
          </h1>
          <p className="text-lg text-slate-600">
            Choose the designated parking type you need to register for
          </p>
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto w-full mb-8">
          {categories.map((category) => {
            const Icon = category.icon;
            const isSelected = selectedCategory === category.id;
            
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`group relative bg-white rounded-2xl p-6 border-2 text-left transition-all duration-300 ${
                  isSelected 
                    ? 'border-blue-500 shadow-lg ring-2 ring-blue-200' 
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                }`}
                style={isSelected ? { background: `linear-gradient(135deg, ${category.bgColor} 0%, rgba(255,255,255,1) 100%)` } : {}}
                data-testid={`category-${category.id}`}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4">
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                  </div>
                )}
                
                <div className="flex items-start gap-4">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: category.bgColor }}
                  >
                    <Icon className="w-7 h-7" style={{ color: category.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {category.name}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Help Text */}
        {selectedCategory && (
          <p className="text-center text-slate-500 mb-4 animate-fade-in">
            {categories.find(c => c.id === selectedCategory)?.requiresOCR 
              ? "You'll need to capture a photo of your Blue Badge on the next screen"
              : "You'll fill in your details on the next screen"
            }
          </p>
        )}
      </main>

      {/* Footer Navigation */}
      <footer className="px-6 py-6 border-t-2 border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/kiosk")}
            className="kiosk-btn-secondary flex items-center gap-3"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>Back</span>
          </button>

          <button
            onClick={handleContinue}
            disabled={!selectedCategory}
            className="kiosk-btn-primary flex items-center gap-3"
            data-testid="continue-button"
          >
            <span>Continue</span>
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </footer>
    </div>
  );
}
